/** Shared content and brand tokens for front page design variations. */

export const SHOW_TRUSTED_BY = false
export const SHOW_TEAM_LINK = false

export const BRAND = {
  primary: '#0677b9',
  secondary: '#7bacd6',
  primaryDark: '#055a8c',
  lightBg: '#F5F5F5',
  logo: '/api/static/box21_logo.svg',
}

export const HERO = {
  line1: 'Make your app see.',
  line2: 'Manage your dataset.',
  line3: 'Train and deploy with ease.',
  tagline: 'Turn visual work into business critical data.',
}

export const PILLARS = [
  {
    id: 'datasets',
    title: 'Create & manage datasets',
    icon: '/api/static/frontpage/images.svg',
    tags: ['Video', 'Images', 'Collaborate'],
  },
  {
    id: 'train',
    title: 'Train vision models',
    icon: '/api/static/frontpage/boxes.svg',
    tags: ['Boxes', 'Keypoints', 'Segmentation'],
  },
  {
    id: 'deploy',
    title: 'Deploy on mobile, edge or GPU',
    icon: '/api/static/frontpage/track.svg',
    tags: ['Count', 'Alert', 'Track'],
  },
  {
    id: 'monitor',
    title: 'Monitor performance',
    icon: '/api/static/frontpage/visualize.svg',
    tags: ['Visualize', 'Validate', 'Improve'],
  },
]

export const SHOWCASE = [
  {
    id: 'healthcare',
    image: '/api/static/examples/medication.png',
    title: 'Safer medication checks in health care',
    text: 'Box21 powers offline medication verification, fully integrated with the largest electronic prescribing provider in the Netherlands. Smart glasses or a phone replace the second pair of eyes — saving care teams over 20,000 hours of manual double checks per year.',
  },
  {
    id: 'lego',
    image: '/api/static/examples/brickhydra.png',
    title: 'Automated LEGO sorting & verification',
    text: 'Vision models sort and verify LEGO parts automatically, so rental businesses ship complete, high-quality sets every time — and scale without a proportional increase in labor.',
  },
  {
    id: 'edge',
    image: 'https://videoapi-muybridge.vimeocdn.com/animated-thumbnails/image/10ea5998-e881-4bb6-845f-db5e6898bfe5.gif?ClientID=sulu&Date=1741791340&Signature=91b4e54e383603464a5afdd2b8257db3bbcfb4cc',
    title: 'Realtime on the edge',
    text: 'Deploy on mobile or edge devices — realtime and offline, wherever your cameras are. An iPhone runs a complete model and analysis in under 20 ms.',
  },
]

export const PARTNERS = [
  { name: 'Wageningen University & Research', logo: '/api/static/wageningen-university-and-research.svg', width: '80%' },
  { name: 'Enovation', logo: '/api/static/frontpage/logo-enovation.svg', width: '30%' },
  { name: 'Utrecht University', logo: '/api/static/utrechtuniversity.svg', width: '80%' },
  { name: 'Friedrich Schiller University Jena', logo: '/api/static/friedrich-schiller-university-jena.png', width: '70%' },
  { name: 'Legotheek Eefde', logo: '/api/static/legotheek-eefde.png', width: '50%' },
]

export const HERO_IMAGE = '/api/static/frontpage/airport-annotated.jpg'

export const WORKFLOW = [
  {
    step: '01',
    title: 'Annotate your data',
    text: 'Label images and video with bounding boxes, polygons, keypoints, and segmentation — solo or with your team.',
  },
  {
    step: '02',
    title: 'Train vision models',
    text: 'Fine-tune YOLO and custom architectures on cloud GPUs. Compare experiments and track metrics in real time.',
  },
  {
    step: '03',
    title: 'Deploy everywhere',
    text: 'Export to ONNX, CoreML, TensorRT, and more. Run on edge devices, mobile, or scalable cloud inference.',
  },
]

export const STATS = [
  { value: '10M+', label: 'Images annotated' },
  { value: '20,000+', label: 'Manual hours automated yearly' },
  { value: '<20ms', label: 'On-device inference' },
  { value: '19', label: 'Export formats' },
]

export const INDUSTRIES = [
  { id: 'healthcare', title: 'Healthcare', text: 'Automated medication verification and process validation — offline and privacy-proof.' },
  { id: 'manufacturing', title: 'Manufacturing & industry', text: 'Count parts, detect anomalies, and validate process steps automatically.' },
  { id: 'robotics', title: 'Robotics & warehousing', text: 'Vision-guided sorting, picking, and inspection that scales without extra labor.' },
  { id: 'agriculture', title: 'Agriculture', text: 'Automated fruit picking, crop monitoring, and livestock tracking.' },
  { id: 'government', title: 'Government & public space', text: 'Automatic parking checks and monitoring — with automated blurring of privacy-sensitive information.' },
  { id: 'science', title: 'Life sciences & research', text: 'Segment and analyse cell structures in microscopy images, monitor wildlife at scale.' },
]

export const FAQ = [
  {
    q: 'What vision tasks does Box21 support?',
    a: 'Detection, instance segmentation, classification, keypoints, and oriented bounding boxes — all from a single platform.',
  },
  {
    q: 'Can I deploy models to edge devices?',
    a: 'Yes. Export to CoreML, ONNX, TensorRT, TFLite, and other formats for mobile, Raspberry Pi, NVIDIA Jetson, and custom hardware.',
  },
  {
    q: 'Can Box21 run on-premise or GDPR-proof?',
    a: 'Yes. Deploy fully local, on your own infrastructure, or in a GDPR-compliant cloud. Your dataset stays private, and privacy-sensitive information can be blurred automatically.',
  },
  {
    q: 'Is Box21 suitable for research teams?',
    a: 'Absolutely. Box21 powers Agouti.eu and research at Wageningen University and Utrecht University — built for collaboration and reproducibility.',
  },
  {
    q: 'How does Box21 compare to training locally?',
    a: 'Cloud-accelerated training with experiment tracking, team review workflows, and one-click deployment — without managing infrastructure.',
  },
]

export const INTEGRATION = [
  {
    id: 'private',
    title: 'Your data stays yours',
    text: 'Manage your custom private dataset — validate existing models and build fully custom ones on top of it.',
  },
  {
    id: 'gdpr',
    title: 'Deploy local or GDPR-proof',
    text: 'Run on your own servers, on edge devices, or in a GDPR-compliant cloud — whatever your compliance requires.',
  },
  {
    id: 'process',
    title: 'Integrated in your process',
    text: 'Not a standalone tool: Box21 plugs into your systems, architecture, and business process end to end.',
  },
  {
    id: 'custom',
    title: 'Built for your requirements',
    text: 'Custom needs like automatic blurring of privacy-sensitive information are part of the solution, not an afterthought.',
  },
]

export const TESTIMONIAL = {
  quote: 'Five minutes per double check may not sound like much, but altogether it amounts to at least 20,000 hours per year. With the medication app, the double check is performed using smart glasses or a mobile phone — making a second pair of eyes unnecessary.',
  name: 'Jan Kees van Wijnen',
  role: 'Board member, tanteLouise',
  source: 'Zorgvisie (2025)',
}

export const TEAM_MEMBERS = [
  {
    id: 'ramon-hollands',
    name: 'Ramon Hollands',
    role: 'Founder & Solution Engineer',
    bio: 'Leads product and AI strategy at Box21 — turning computer vision research into production systems for healthcare, industry, ecology and robotics.',
    photo: '/api/static/team/ramon-hollands.png',
    photoPosition: 'center 15%',
  },
  {
    id: 'ramon-fano',
    name: 'Ramon Fano',
    role: 'Solution Engineer',
    bio: 'Translates business processes into vision AI solutions — from first intake to a fully integrated deployment that fits your architecture.',
    photo: '/api/static/team/ramon-fano.png',
    photoPosition: 'center 20%',
  },
  {
    id: 'kars-kingma',
    name: 'Kars Kingma',
    role: 'Business Growth Manager',
    bio: 'Drives Box21’s growth — building partnerships and helping new customers find the fastest route from raw images to deployed vision AI.',
    photo: '/api/static/team/kars-kingma.png',
    photoPosition: 'center 15%',
  },
  {
    id: 'jorrit-van-gils',
    name: 'Jorrit van Gils',
    role: 'Partner · Machine Learning Specialist',
    bio: 'Partnered machine learning specialist — designs and tunes model architectures and training pipelines to push accuracy while keeping inference fast at the edge.',
    photo: '/api/static/team/jorrit-van-gils.png',
    photoPosition: 'center 15%',
    link: 'https://jorritvangils.com/',
  },
]

export const DESIGN_VARIANTS = [
  {
    id: 'tauri',
    name: 'Tauri',
    subtitle: 'Dark, developer-focused hero with glowing accents',
    inspiration: 'https://v2.tauri.app/',
    component: 'DesignTauri',
  },
  {
    id: 'dropbox',
    name: 'Dropbox',
    subtitle: 'Clean whitespace, friendly alternating sections',
    inspiration: 'https://www.dropbox.com/',
    component: 'DesignDropbox',
  },
  {
    id: 'bento',
    name: 'Bento Grid',
    subtitle: 'Modern asymmetric card layout',
    inspiration: 'Original',
    component: 'DesignBento',
  },
  {
    id: 'nature',
    name: 'Wildlife Canvas',
    subtitle: 'Immersive nature photography with organic flow',
    inspiration: 'Original',
    component: 'DesignNature',
  },
  {
    id: 'terminal',
    name: 'Dev Terminal',
    subtitle: 'Code-aesthetic pipeline for technical teams',
    inspiration: 'Original',
    component: 'DesignTerminal',
  },
  {
    id: 'split',
    name: 'Split Vision',
    subtitle: 'Bold half-and-half hero with live annotations',
    inspiration: 'Original',
    component: 'DesignSplit',
  },
  {
    id: 'glass',
    name: 'Frosted Field',
    subtitle: 'Glassmorphism over blurred wildlife imagery',
    inspiration: 'Original',
    component: 'DesignGlass',
  },
  {
    id: 'swiss',
    name: 'Swiss Grid',
    subtitle: 'Typography-first minimal grid system',
    inspiration: 'Original',
    component: 'DesignSwiss',
  },
  {
    id: 'dashboard',
    name: 'Product Preview',
    subtitle: 'Hero framed as the platform itself',
    inspiration: 'Original',
    component: 'DesignDashboard',
  },
  {
    id: 'pipeline',
    name: 'Workflow Pipeline',
    subtitle: 'Horizontal annotate → train → deploy journey',
    inspiration: 'Original',
    component: 'DesignPipeline',
  },
]
